import {
    MousePointerClick,
    Clock,
    ClipboardCheck,
    Pencil,
    FileSearch,
    GitCompareArrows,
    Fingerprint,
    MapPin,
    Briefcase,
    ClipboardList,
    Send,
    FileText,
    ShieldCheck,
    Sparkles,
    ArrowRight,
    ChevronDown,
    ChevronUp,
    Info,
} from "lucide-react"

import {
    GuideDialog,
    BrowserFrame,
    Chip,
    MockButton,
    ClickHint,
    TakeAways,
    StepHeading,
    Lead,
    LifecycleStrip,
} from "../GuideKit"

/*
 * QaGuideDialog — a near full-screen, step-by-step walkthrough for the QA
 * reviewer (an ADMIN user with the "qa" scope). It covers picking up a submitted
 * TruCheck, reviewing each verification section, resolving it as Clear / Review
 * Required, sending the consumer report, and handing off to HR for adjudication.
 * Shared UI lives in ../GuideKit; this file only defines the QA-specific steps.
 */

// The only two real statuses QA works between: a case sits at "TruCheck Review" while it
// waits for your review, and moves to "Ready for Adjudication" once you're done.
const LIFECYCLE = [
    { label: "TruCheck Review", tone: "amber", icon: Clock },
    { label: "Ready for Adjudication", tone: "red", icon: ClipboardCheck },
]

// A mock of a review section header: icon + title on the left, and the
// QA-status control (pencil + status chip) on the right — exactly how
// VerificationQaStatus renders inside each SectionCard.
const ReviewSectionMock = ({ icon: Icon, title, status = "TruCheck Review", tone = "amber", pulse }) => (
    <div className="flex items-center justify-between rounded-xl border border-gray-200 bg-white px-4 py-3">
        <span className="inline-flex items-center gap-2 text-sm font-semibold text-gray-800">
            <Icon className="w-4 h-4 text-gray-500" /> {title}
        </span>
        <span className="flex items-center gap-2">
            <span className="relative inline-flex">
                {pulse && <span className="absolute inset-0 rounded-md bg-indigo-400/40 animate-ping" />}
                <span className="relative inline-flex items-center justify-center w-7 h-7 rounded-md border border-indigo-200 bg-indigo-50 text-indigo-600">
                    <Pencil className="w-3.5 h-3.5" />
                </span>
            </span>
            <Chip tone={tone}>{status}</Chip>
        </span>
    </div>
)

// A collapsible review-section card, mirroring the real SectionCard: a gradient
// icon + title, the header controls on the right (optional action buttons, the
// pencil, the QA-status chip, and a chevron), and — when expanded — a body.
const PencilBox = ({ pulse }) => (
    <span className="relative inline-flex">
        {pulse && <span className="absolute inset-0 rounded-md bg-indigo-400/40 animate-ping" />}
        <span className="relative inline-flex items-center justify-center w-7 h-7 rounded-md border border-indigo-200 bg-indigo-50 text-indigo-600">
            <Pencil className="w-3.5 h-3.5" />
        </span>
    </span>
)

const SectionCardMock = ({ icon: Icon, title, status = "TruCheck Review", tone = "amber", actions = null, expanded = false, pulse = false, children = null }) => (
    <div className="rounded-xl border-2 border-gray-200 bg-white overflow-hidden">
        <div className="flex items-center justify-between gap-2 flex-wrap p-3 bg-gray-50">
            <span className="inline-flex items-center gap-2.5 min-w-0 text-sm font-bold text-gray-900">
                <span className="w-7 h-7 rounded-lg bg-gradient-to-br from-gray-400 to-gray-600 flex items-center justify-center shrink-0">
                    <Icon className="w-3.5 h-3.5 text-white" />
                </span>
                <span className="truncate">{title}</span>
            </span>
            <span className="flex items-center gap-2 flex-wrap">
                {actions}
                <PencilBox pulse={pulse} />
                <Chip tone={tone}>{status}</Chip>
                {expanded
                    ? <ChevronUp className="w-4 h-4 text-gray-500" />
                    : <ChevronDown className="w-4 h-4 text-gray-500" />}
            </span>
        </div>
        {expanded && children && <div className="p-4 border-t border-gray-100">{children}</div>}
    </div>
)

const STEPS = [
    {
        nav: "Overview",
        phase: "The big picture",
        render: () => (
            <div>
                <StepHeading>What QA does on a TruCheck</StepHeading>
                <Lead>
                    Once a candidate finishes their submission, the case sits at{" "}
                    <Chip tone="amber" icon={Clock}>TruCheck Review</Chip> — that’s your signal it’s ready for
                    QA. Your job is to review each verification section and resolve it as{" "}
                    <Chip tone="green">Clear</Chip> or <Chip tone="red">Review Required</Chip>. Those
                    resolutions set the TruCheck’s overall <strong>flag</strong>, and once you’re done
                    the case moves to <strong>Ready for Adjudication</strong> for the client (HR) to decide.
                </Lead>

                <div className="rounded-xl border border-gray-200 bg-white p-4 mb-4">
                    <LifecycleStrip stages={LIFECYCLE} />
                    <p className="text-xs text-gray-500 mt-2">
                        These are the only two statuses in play: <strong>TruCheck Review</strong> means it’s
                        waiting for your review; your work is what advances it to{" "}
                        <strong>Ready for Adjudication</strong>.
                    </p>
                </div>

                <div className="grid sm:grid-cols-2 gap-3">
                    <div className="rounded-xl border border-indigo-200 bg-indigo-50/60 p-4">
                        <p className="text-sm font-semibold text-indigo-900 mb-1 flex items-center gap-2">
                            <Sparkles className="w-4 h-4" /> What you do
                        </p>
                        <p className="text-sm text-indigo-800/90">
                            Inspect the document analysis, cross-check, SSN, geo &amp; work status;
                            mark each Clear or Review Required (with a comment); send the consumer report.
                        </p>
                    </div>
                    <div className="rounded-xl border border-gray-200 bg-gray-50 p-4">
                        <p className="text-sm font-semibold text-gray-800 mb-1 flex items-center gap-2">
                            <ShieldCheck className="w-4 h-4" /> What HR does after you
                        </p>
                        <p className="text-sm text-gray-600">
                            Reads your flag and report, then makes the final Eligible / Not Eligible call.
                            You set the evidence; HR owns the decision.
                        </p>
                    </div>
                </div>
            </div>
        ),
    },
    {
        nav: "Pick up a submission",
        phase: "Step 1 · Open the case",
        render: () => (
            <div>
                <StepHeading>Open a submitted TruCheck</StepHeading>
                <Lead>
                    On the <strong>TruCheck</strong> list, filter to{" "}
                    <Chip tone="amber" icon={Clock}>TruCheck Review</Chip> to find cases the candidate has
                    submitted, then click a row to open the detail page.
                </Lead>

                <BrowserFrame>
                    <div className="rounded-lg border border-gray-200 bg-white divide-y divide-gray-100">
                        <div className="flex items-center justify-between px-3 py-3 hover:bg-indigo-50/50 cursor-pointer">
                            <div className="flex items-center gap-3">
                                <MousePointerClick className="w-4 h-4 text-indigo-600 animate-pulse" />
                                <span className="text-sm font-medium text-gray-800">TC-1044 · John Doe</span>
                            </div>
                            <Chip tone="amber" icon={Clock}>TruCheck Review</Chip>
                        </div>
                        <div className="flex items-center justify-between px-3 py-3">
                            <span className="text-sm text-gray-700">TC-1043 · Priya Nair</span>
                            <Chip tone="amber" icon={Clock}>TruCheck Review</Chip>
                        </div>
                    </div>
                </BrowserFrame>

                <div className="mt-3">
                    <ClickHint>Click a TruCheck Review row to start reviewing.</ClickHint>
                </div>
            </div>
        ),
    },
    {
        nav: "Your workspace",
        phase: "Step 2 · The review sections",
        render: () => (
            <div>
                <StepHeading>The Case Summary is just context — the sections below are your work</StepHeading>
                <Lead>
                    The <strong>Case Summary</strong> at the top is only reference info (and the Consent
                    PDF, which you rarely need). Your actual work is <strong>everything below it</strong>:
                    a stack of review sections. Each is a <strong>collapsible card</strong> — even while
                    collapsed, its header shows a <Chip tone="amber">TruCheck Review</Chip> status so you can
                    scan at a glance which items still need you.
                </Lead>

                <BrowserFrame url="app.trateverified.com/pre-check/TC-1044">
                    {/* Case Summary — deliberately muted, it's just context. */}
                    <div className="rounded-lg border border-gray-200 bg-white/70 px-4 py-2.5 mb-3 flex items-center justify-between gap-2 flex-wrap opacity-70">
                        <span className="text-sm font-semibold text-gray-500">Case Summary · TC-1044 · John Doe</span>
                        <span className="text-[11px] text-gray-400">reference info + Consent PDF</span>
                    </div>

                    <p className="text-[11px] font-semibold uppercase tracking-wide text-gray-400 mb-2 px-1">
                        Review sections — your workspace
                    </p>

                    <div className="space-y-2.5">
                        {/* Collapsed, still shows status + inline document buttons. */}
                        <SectionCardMock
                            icon={FileSearch}
                            title="Document Analysis"
                            status="TruCheck Review"
                            tone="amber"
                            pulse
                            actions={<MockButton tone="ghost" icon={FileText}>View Document</MockButton>}
                        />
                        {/* Expanded, showing that details live inside. */}
                        <SectionCardMock
                            icon={GitCompareArrows}
                            title="Cross Check"
                            status="TruCheck Review"
                            tone="amber"
                            expanded
                        >
                            <div className="grid sm:grid-cols-2 gap-2 text-xs text-gray-500">
                                <div className="rounded-lg border border-gray-100 bg-gray-50 px-3 py-2">Documents compared · Passport ↔ SSN Card</div>
                                <div className="rounded-lg border border-gray-100 bg-gray-50 px-3 py-2">Comparison results · Name, DOB match</div>
                            </div>
                        </SectionCardMock>
                        {/* Already resolved — status reads Clear. */}
                        <SectionCardMock icon={MapPin} title="Geo Location" status="Clear" tone="green" />
                    </div>
                </BrowserFrame>

                <div className="mt-3">
                    <TakeAways
                        items={[
                            "A collapsed header already tells you the item's status — TruCheck Review means it's waiting on your action.",
                            "Open document / verification details straight from the header, without expanding.",
                            "Expand a card (chevron) to read the full breakdown before you resolve it.",
                            "The Case Summary and Consent PDF up top are context — not where you decide.",
                        ]}
                    />
                </div>
            </div>
        ),
    },
    {
        nav: "How to resolve a section",
        phase: "Step 3 · The core action",
        render: () => (
            <div>
                <StepHeading>Resolving a section: the pattern you’ll repeat</StepHeading>
                <Lead>
                    Every review section carries a QA status. Click the{" "}
                    <span className="inline-flex items-center justify-center w-5 h-5 rounded border border-indigo-200 bg-indigo-50 text-indigo-600 align-middle"><Pencil className="w-3 h-3" /></span>{" "}
                    pencil in its header, choose <strong>Clear</strong> or{" "}
                    <strong>Review Required</strong>, add a <strong>comment</strong> (required), and save.
                </Lead>

                <ReviewSectionMock icon={FileSearch} title="Document Analysis" pulse />

                <div className="my-3 flex justify-center">
                    <ArrowRight className="w-5 h-5 text-gray-300 rotate-90" />
                </div>

                <div className="rounded-xl border border-gray-200 bg-white shadow-sm p-4 max-w-md mx-auto">
                    <p className="text-sm font-semibold text-gray-800 mb-3">Edit QA Status</p>
                    <div className="space-y-2 mb-3">
                        <div className="flex items-center gap-3 px-3 py-2 rounded-lg border border-green-300 bg-green-50">
                            <span className="w-4 h-4 rounded-full border-2 border-green-500 bg-green-500" />
                            <span className="text-sm font-medium text-gray-700">Clear</span>
                        </div>
                        <div className="flex items-center gap-3 px-3 py-2 rounded-lg border border-gray-200">
                            <span className="w-4 h-4 rounded-full border-2 border-gray-300" />
                            <span className="text-sm font-medium text-gray-700">Review Required</span>
                        </div>
                    </div>
                    <div className="rounded-md border border-gray-300 bg-white px-3 py-2 text-xs text-gray-400 mb-3">
                        Comment (required) — e.g. “ID matches, document authentic.”
                    </div>
                    <div className="flex justify-end gap-2">
                        <MockButton tone="ghost">Cancel</MockButton>
                        <MockButton tone="indigo" pulse>Save</MockButton>
                    </div>
                </div>

                <div className="mt-3 rounded-xl border border-gray-200 bg-gray-50 p-3 flex items-center gap-2 text-sm text-gray-600">
                    <Info className="w-4 h-4 text-gray-400" />
                    A comment is mandatory — Save stays disabled until you add one.
                </div>
            </div>
        ),
    },
    {
        nav: "Document Analysis",
        phase: "Step 4 · Review sections",
        render: () => (
            <div>
                <StepHeading>Document Analysis</StepHeading>
                <Lead>
                    Each submitted document has an AI analysis card. Open{" "}
                    <strong>View Document</strong> and <strong>View Verification</strong> to inspect the
                    source, check the extracted fields and validation checks, then set the QA status.
                </Lead>

                <div className="rounded-xl border border-gray-200 bg-white p-4">
                    <div className="flex items-center justify-between gap-2 flex-wrap mb-3">
                        <span className="inline-flex items-center gap-2 text-sm font-semibold text-gray-800">
                            <FileSearch className="w-4 h-4 text-gray-500" /> Passport — Analysis
                        </span>
                        <span className="flex items-center gap-2">
                            <MockButton tone="ghost" icon={FileText}>View Document</MockButton>
                            <span className="inline-flex items-center justify-center w-7 h-7 rounded-md border border-indigo-200 bg-indigo-50 text-indigo-600">
                                <Pencil className="w-3.5 h-3.5" />
                            </span>
                            <Chip tone="green">Clear</Chip>
                        </span>
                    </div>
                    <div className="grid sm:grid-cols-2 gap-2 text-xs text-gray-500">
                        <div className="rounded-lg border border-gray-100 bg-gray-50 px-3 py-2">Extracted fields · Name, DOB, Doc No.</div>
                        <div className="rounded-lg border border-gray-100 bg-gray-50 px-3 py-2">Validation checks · MRZ, expiry, tampering</div>
                    </div>
                </div>

                <div className="mt-3">
                    <TakeAways
                        items={[
                            "Repeat for every document card in the section.",
                            "Set Clear when the document is authentic and matches the candidate.",
                            "Set Review Required (with a reason) when anything looks off.",
                        ]}
                    />
                </div>
            </div>
        ),
    },
    {
        nav: "Cross-check & identity",
        phase: "Step 5 · Review sections",
        render: () => (
            <div>
                <StepHeading>Cross Check, SSN &amp; identity</StepHeading>
                <Lead>
                    Work down the identity sections and resolve each one. For <strong>SSN
                    Verification</strong> you can also <strong>Run New Session</strong> if a fresh check
                    is needed before you resolve it.
                </Lead>

                <div className="space-y-2.5">
                    <ReviewSectionMock icon={GitCompareArrows} title="Cross Check" status="Clear" tone="green" />
                    <div className="flex items-center justify-between rounded-xl border border-gray-200 bg-white px-4 py-3">
                        <span className="inline-flex items-center gap-2 text-sm font-semibold text-gray-800">
                            <Fingerprint className="w-4 h-4 text-gray-500" /> SSN Verification
                        </span>
                        <span className="flex items-center gap-2">
                            <MockButton tone="ghost">Run New Session</MockButton>
                            <span className="inline-flex items-center justify-center w-7 h-7 rounded-md border border-indigo-200 bg-indigo-50 text-indigo-600">
                                <Pencil className="w-3.5 h-3.5" />
                            </span>
                            <Chip tone="amber">TruCheck Review</Chip>
                        </span>
                    </div>
                </div>

                <div className="mt-3 rounded-xl border border-gray-200 bg-gray-50 p-3 flex items-center gap-2 text-sm text-gray-600">
                    <Info className="w-4 h-4 text-gray-400" />
                    The comparison documents and results are shown inside each section to support your call.
                </div>
            </div>
        ),
    },
    {
        nav: "Geo & work status",
        phase: "Step 6 · Review sections",
        render: () => (
            <div>
                <StepHeading>Geo Location &amp; Work Status</StepHeading>
                <Lead>
                    Resolve <strong>Geo Location</strong> like the other sections. For{" "}
                    <strong>Work Status</strong>, you can <strong>Override Work Status</strong> when the
                    determined employment authorization needs correcting — every override is kept in a
                    history log.
                </Lead>

                <div className="space-y-2.5">
                    <ReviewSectionMock icon={MapPin} title="Geo Location" status="Clear" tone="green" />
                    <div className="flex items-center justify-between rounded-xl border border-gray-200 bg-white px-4 py-3">
                        <span className="inline-flex items-center gap-2 text-sm font-semibold text-gray-800">
                            <Briefcase className="w-4 h-4 text-gray-500" /> Work Status
                        </span>
                        <MockButton tone="ghost" icon={Pencil}>Override Work Status</MockButton>
                    </div>
                </div>

                <div className="mt-3">
                    <TakeAways
                        items={[
                            "Geo Location confirms where the candidate verified from.",
                            "Work Status shows the initial (client-provided) vs. the effective determination.",
                            "Overrides are recorded with your comment and timestamp.",
                        ]}
                    />
                </div>
            </div>
        ),
    },
    {
        nav: "Send consumer report",
        phase: "Step 7 · Deliver",
        render: () => (
            <div>
                <StepHeading>Send the Consumer Report</StepHeading>
                <Lead>
                    Once the case is completed you can open the full{" "}
                    <strong>Consumer Report</strong> and, as QA, <strong>send it to the candidate</strong>.
                    HR sees only the “View” button — sending is a QA-only action.
                </Lead>

                <div className="rounded-xl border border-gray-200 bg-white p-4">
                    <div className="flex items-center gap-2 mb-3">
                        <ClipboardList className="w-4 h-4 text-gray-500" />
                        <span className="text-sm font-semibold text-gray-800">TruCheck Consumer Report</span>
                    </div>
                    <div className="flex gap-2 flex-wrap">
                        <MockButton tone="ghost" icon={FileText}>View TruCheck Consumer Report</MockButton>
                        <MockButton tone="indigo" icon={Send} pulse>Send Consumer Report to Candidate</MockButton>
                    </div>
                </div>

                <div className="mt-3 rounded-xl border border-gray-200 bg-gray-50 p-3 flex items-center gap-2 text-sm text-gray-600">
                    <Info className="w-4 h-4 text-gray-400" />
                    Sent reports are tracked in a history list so you can see what went out and when.
                </div>
            </div>
        ),
    },
    {
        nav: "Hand off to HR",
        phase: "Step 8 · Done",
        render: () => (
            <div>
                <StepHeading>You’ve set the flag — HR takes it from here</StepHeading>
                <Lead>
                    Your section resolutions roll up into the TruCheck’s overall flag —{" "}
                    <Chip tone="green">Clear</Chip> if everything passed, or{" "}
                    <Chip tone="red">Review Required</Chip> if anything needs attention. The case then
                    shows as <Chip tone="red" icon={ClipboardCheck}>Ready for Adjudication</Chip> for HR.
                </Lead>

                <div className="rounded-xl border border-gray-200 bg-white p-4 mb-4">
                    <LifecycleStrip stages={LIFECYCLE} activeIndex={1} />
                </div>

                <div className="rounded-xl border border-indigo-200 bg-indigo-50/60 p-4">
                    <p className="text-sm font-semibold text-indigo-900 mb-2">Quick reference</p>
                    <TakeAways
                        items={[
                            "Open a TruCheck Review case from the TruCheck list.",
                            "Resolve every section: Document Analysis, Cross Check, SSN, Geo, Work Status.",
                            "Each resolution is Clear or Review Required — with a required comment.",
                            "Send the consumer report to the candidate.",
                            "Your resolutions set the flag → HR makes the final Eligible / Not Eligible call.",
                        ]}
                    />
                </div>
            </div>
        ),
    },
]

const QaGuideDialog = ({ onClose }) => (
    <GuideDialog
        eyebrow="QA Guide"
        title="Reviewing a TruCheck"
        steps={STEPS}
        onClose={onClose}
        accent="indigo"
    />
)

export default QaGuideDialog
